import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import type { PublicUser } from '@wishare/shared';
import { api, ApiError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { Avatar, Badge, Button, Confirm, EmptyState, Notice, TextField } from '../components/ui.js';
import { SkeletonRows } from '../components/Skeleton.js';
import { IconFriends } from '../components/icons.js';

/**
 * Друзья.
 *
 * Дружба здесь — это ключ от списков, поэтому подтверждение обязательно
 * двустороннее. Экран собран вокруг одного действия: найти человека и позвать.
 */

export function FriendsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { refresh } = useAuth();
  const [query, setQuery] = useState('');
  /** Кого собираемся заблокировать — блокировка требует подтверждения. */
  const [blocking, setBlocking] = useState<PublicUser | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['friends'], queryFn: api.friends.all });

  const { data: results, isFetching: searching } = useQuery({
    queryKey: ['users', 'search', query],
    queryFn: () => api.users.search(query),
    // Ищем только осмысленные запросы: с одной буквы выдача бесполезна.
    enabled: query.trim().length >= 2,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['friends'] });
    void queryClient.invalidateQueries({ queryKey: ['users', 'search'] });
    void queryClient.invalidateQueries({ queryKey: ['lists', 'feed'] });
    void refresh();
  };

  const sendRequest = useMutation({
    mutationFn: (userId: string) => api.friends.request({ userId }),
    onSuccess: invalidate,
  });
  const accept = useMutation({ mutationFn: api.friends.accept, onSuccess: invalidate });
  const decline = useMutation({ mutationFn: api.friends.decline, onSuccess: invalidate });
  const remove = useMutation({ mutationFn: api.friends.remove, onSuccess: invalidate });
  const block = useMutation({
    mutationFn: api.friends.block,
    onSuccess: () => {
      invalidate();
      setBlocking(null);
    },
  });

  /* Шапка данных не ждёт — пишем один раз и показываем в обоих состояниях. */
  const шапка = (
    <div className="page-header">
      <div>
        <h1>Друзья</h1>
        <p className="muted small">Только друзья видят ваши списки — и только их списки видите вы.</p>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <>
        {шапка}
        <SkeletonRows label="Загружаем друзей" />
      </>
    );
  }

  const { friends = [], incoming = [], outgoing = [] } = data ?? {};

  return (
    <>
      {шапка}

      <div className="stack stack--lg">
        <section className="stack">
          <TextField
            label="Найти человека"
            placeholder="Имя или @username"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            hint="Поиск идёт по имени и по имени пользователя. По почте людей не ищем — это было бы небезопасно."
          />

          {searching && <span className="small muted">Ищем…</span>}

          {query.trim().length >= 2 && !searching && (results ?? []).length === 0 && (
            <p className="small faint">Никого не нашли. Проверьте написание.</p>
          )}

          {(results ?? []).length > 0 && (
            <div className="stack stack--sm">
              {(results ?? []).map((user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  action={
                    user.friendship?.status === 'accepted' ? (
                      <Badge tone="success">уже друзья</Badge>
                    ) : user.friendship?.status === 'pending' ? (
                      <Badge>{user.friendship.outgoing ? 'заявка отправлена' : 'ждёт вашего ответа'}</Badge>
                    ) : (
                      <Button
                        size="sm"
                        variant="primary"
                        loading={sendRequest.isPending && sendRequest.variables === user.id}
                        onClick={() => sendRequest.mutate(user.id)}
                      >
                        Позвать
                      </Button>
                    )
                  }
                />
              ))}
            </div>
          )}

          {sendRequest.error instanceof ApiError && (
            <Notice tone="danger">
              {sendRequest.error.message}
            </Notice>
          )}
        </section>

        {incoming.length > 0 && (
          <section className="stack">
            <h2>Хотят дружить</h2>
            <div className="stack stack--sm">
              {incoming.map((entry) => (
                <UserRow
                  key={entry.id}
                  user={entry.user}
                  action={
                    <div className="row" style={{ gap: 'var(--space-2)' }}>
                      <Button size="sm" variant="primary" onClick={() => accept.mutate(entry.user.id)}>
                        Принять
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => decline.mutate(entry.user.id)}>
                        Нет
                      </Button>
                    </div>
                  }
                />
              ))}
            </div>
          </section>
        )}

        {outgoing.length > 0 && (
          <section className="stack">
            <h2>Ждём ответа</h2>
            <div className="stack stack--sm">
              {outgoing.map((entry) => (
                <UserRow
                  key={entry.id}
                  user={entry.user}
                  action={
                    <Button size="sm" variant="ghost" onClick={() => decline.mutate(entry.user.id)}>
                      Отозвать
                    </Button>
                  }
                />
              ))}
            </div>
          </section>
        )}

        <section className="stack">
          <h2>Мои друзья {friends.length > 0 && <span className="faint">· {friends.length}</span>}</h2>

          {friends.length === 0 ? (
            <EmptyState
              icon={<IconFriends />}
              title="Друзей пока нет"
              text="Найдите знакомых через поиск выше. Как только они подтвердят заявку, вы увидите их списки желаний."
            />
          ) : (
            <div className="stack stack--sm">
              {friends.map((entry) => (
                <UserRow
                  key={entry.id}
                  user={entry.user}
                  action={
                    <div className="row" style={{ gap: 'var(--space-2)' }}>
                      <Button size="sm" variant="ghost" onClick={() => remove.mutate(entry.user.id)}>
                        Удалить
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="btn--icon"
                        aria-label="Заблокировать"
                        title="Заблокировать"
                        onClick={() => setBlocking(entry.user)}
                      >
                        🚫
                      </Button>
                    </div>
                  }
                />
              ))}
            </div>
          )}
        </section>
      </div>

      {/*
        Блокировка сильнее удаления из друзей: она закрывает доступ ко всему,
        включая публичные списки, и не даёт подать заявку заново. Действие
        серьёзное, поэтому спрашиваем подтверждение и объясняем последствия.
      */}
      <Confirm
        open={Boolean(blocking)}
        title="Заблокировать?"
        message={
          blocking
            ? `${blocking.displayName} перестанет видеть ваши списки — все, включая публичные — и не сможет отправить вам заявку. Вы тоже не увидите его списки.`
            : ''
        }
        confirmLabel="Заблокировать"
        loading={block.isPending}
        onCancel={() => setBlocking(null)}
        onConfirm={() => blocking && block.mutate(blocking.id)}
      />
    </>
  );
}

function UserRow({ user, action }: { user: PublicUser; action: React.ReactNode }): JSX.Element {
  // На человека с именем пользователя можно нажать и посмотреть его списки.
  // У кого имени нет, профиль открыть нечем — показываем обычным текстом.
  const name = (
    <>
      <div className="strong small truncate">{user.displayName}</div>
      {user.username && <div className="tiny faint truncate">@{user.username}</div>}
    </>
  );

  return (
    <div className="card">
      <div className="row" style={{ padding: 'var(--space-3)' }}>
        <Avatar name={user.displayName} src={user.avatarUrl} />
        <div className="grow" style={{ minWidth: 0 }}>
          {user.username ? (
            <Link to={`/u/${user.username}`} style={{ color: 'inherit' }}>
              {name}
            </Link>
          ) : (
            name
          )}
        </div>
        {action}
      </div>
    </div>
  );
}
